import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Home path for each role — used when an authed user hits a route outside their
// role scope. We redirect to their own home (not /login), so the session stays
// intact from their perspective.
const ROLE_HOME = {
  admin: '/admin',
  spl: '/admin',
  annotator: '/jobs',
  client: '/client',
  god: '/god',
};

/**
 * RoleRoute — extends ProtectedRoute with role-based access control.
 *
 * Usage:
 *   <RoleRoute allowedRoles={['admin', 'spl', 'god']}>
 *     <AdminPage />
 *   </RoleRoute>
 */
export default function RoleRoute({ children, allowedRoles = [] }) {
  const { user, loading, role, effectiveRole } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh', background: '#F3F4F6',
        fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-muted)', letterSpacing: '0.05em',
      }}>
        JUUNI AI
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but wrong role — redirect to the user's own role-home, NOT /login.
  // Dumping an authed user on /login was a session-integrity bug (they'd think they
  // got signed out, re-enter credentials, bounce back). See hostile-test-claude.md Bug 37.
  if (allowedRoles.length > 0 && !allowedRoles.includes(effectiveRole)) {
    // God mode users can access everything (even while impersonating)
    if (role === 'god') return children;

    const home = ROLE_HOME[effectiveRole] || '/';
    return <Navigate to={home} replace />;
  }

  return children;
}
